import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminService } from '../../../services/adminService';
import { billingService } from '../../../services/billingService';
import { usePlanLimits } from '../../../hooks/usePlanLimits';
import {
  formatLimit, formatStorageLimit, getPlanFeatures, PLAN_DISPLAY,
  PLAN_LIMITS, PLAN_ORDER,
} from '../../../config/planFeatures';

// Info popup for features
const FeatureInfoPopup = ({ text }) => {
  const [show, setShow] = useState(false);
  if (!text) return null;
  return (
    <span className="relative inline-flex items-center ml-1">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="w-3.5 h-3.5 rounded-full bg-muted border border-border text-muted-foreground text-xs flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-colors"
        aria-label="More info"
      >
        i
      </button>
      {show && (
        <span className="absolute left-5 top-0 z-50 w-48 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none leading-relaxed">
          {text}
          <span className="absolute left-0 top-2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45" />
        </span>
      )}
    </span>
  );
};

const FALLBACK_PLANS = PLAN_ORDER.map((planKey) => {
  const display = PLAN_DISPLAY[planKey];
  const limits = PLAN_LIMITS[planKey];
  return {
    id: planKey,
    planKey,
    tierName: display.label,
    price: display.price,
    annualPrice: display.annualPrice,
    annualMonthlyPrice: display.annualMonthlyPrice,
    description: display.description,
    isPopular: display.isPopular,
    badgeText: display.badge,
    ctaText: display.ctaText,
    maxForms: limits.maxForms,
    maxSubmissions: limits.maxSubmissions,
    maxTeamMembers: limits.maxTeamMembers,
    maxStorageMb: limits.maxStorageMb,
    features: getPlanFeatures(planKey).map((feature) => ({
      key: feature.key,
      text: feature.label,
      included: feature.plans[planKey] !== false,
      infoPopup: feature.description,
    })),
  };
});

// Derive bar colour class from percentage
const barColorClass = (pct) => {
  if (pct >= 100) return 'bg-error';
  if (pct >= 80)  return 'bg-warning';
  return 'bg-success';
};

// Single usage row: label, icon, current, max, pct, showUpgrade
const UsageRow = ({ label, icon, current, max, pct, showUpgrade }) => {
  const isUnlimited = max === -1;
  const barColor    = barColorClass(pct);
  const textColor   = pct >= 100 ? 'text-error' : pct >= 80 ? 'text-warning' : 'text-foreground';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name={icon} size={15} color="var(--color-muted-foreground)" />
          <span className="text-sm text-muted-foreground truncate">{label}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`text-sm font-semibold ${textColor}`}>
            {typeof current === 'number' ? current.toLocaleString() : current}
            {!isUnlimited && (
              <span className="font-normal text-muted-foreground">
                {' '}/ {formatLimit(max)}
              </span>
            )}
            {isUnlimited && (
              <span className="font-normal text-muted-foreground"> / Unlimited</span>
            )}
          </span>
          {showUpgrade && (
            <Link
              to="/account-settings?tab=billing"
              className="text-xs font-semibold text-primary border border-primary/30 px-2.5 py-1 rounded-lg hover:bg-primary/5 transition-colors whitespace-nowrap"
            >
              Upgrade
            </Link>
          )}
        </div>
      </div>
      {!isUnlimited && (
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${barColor}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
};

const BillingTab = () => {
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Live plan usage
  const {
    usage, limits, loading: usageLoading,
    formUsagePct, submissionUsagePct,
    isAtFormLimit, isNearFormLimit,
    isAtSubLimit, isNearSubLimit,
    planType,
    billing, billingLoading,
  } = usePlanLimits();

  const teamMax = limits.maxTeamMembers ?? -1;
  const teamPct = teamMax === -1 ? 0 : Math.min((usage.teamMembers / teamMax) * 100, 100);
  const isNearTeam = teamMax !== -1 && teamPct >= 80;
  const isAtTeam   = teamMax !== -1 && usage.teamMembers >= teamMax;
  const showUpgrade = isNearFormLimit() || isAtFormLimit() || isNearSubLimit() || isAtSubLimit() || isNearTeam || isAtTeam;

  // Real subscription info from usePlanLimits (Stripe cache → plan config fallback).
  // Any unavailable value renders as a dash.
  const planName = billing?.planName ?? '–';
  const priceLabel = (() => {
    if (typeof billing?.price !== 'number') return '–';
    let amount;
    try {
      amount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (billing?.currency || 'usd').toUpperCase(),
        minimumFractionDigits: Number.isInteger(billing.price) ? 0 : 2,
      }).format(billing.price);
    } catch {
      amount = `${billing.price} ${(billing?.currency || '').toUpperCase()}`.trim();
    }
    return `${amount}/${billing?.interval === 'yearly' ? 'year' : 'month'}`;
  })();
  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };
  // A cancelling subscription has no renewal — show its end date instead.
  const endsLabel = billing?.cancelAtPeriodEnd ? formatDate(billing?.endsAt) : null;
  const nextBillingLabel = formatDate(billing?.nextBillingDate) ?? '–';

  const paymentMethod = { type: 'Visa', last4: '4242', expiry: '12/2027', isDefault: true };

  // Real billing history from Stripe
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState(null);

  useEffect(() => {
    loadPlans();
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    setInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const data = await billingService.getMyInvoices();
      setInvoices(data);
    } catch (err) {
      console.error('Failed to load billing history:', err);
      setInvoicesError('We couldn\u2019t load your billing history. Please try again.');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const data = await adminService?.getAllPricingTiers?.();
      if (data && data?.length > 0) {
        setPlans(data);
      }
    } catch {
      // Use fallback plans
    } finally {
      setLoadingPlans(false);
    }
  };

  const getUpgradeHref = (plan) => {
    if (!plan?.price) return '/authentication';
    const planKey = plan?.planKey || (PLAN_ORDER.includes(plan?.id) ? plan.id : null);
    return planKey
      ? `/support-ticket-system?topic=plan-upgrade&plan=${encodeURIComponent(planKey)}&billing=${encodeURIComponent(billingCycle)}`
      : '/authentication';
  };

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Current Plan & Usage */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              Current Plan: {billingLoading ? '…' : planName}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              {billingLoading
                ? '…'
                : endsLabel
                  ? <>{priceLabel} • Ends: {endsLabel}</>
                  : <>{priceLabel} • Next billing: {nextBillingLabel}</>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="default" iconName="Download" iconPosition="left">
              Download Invoice
            </Button>
            <Button variant="destructive" size="default" iconName="XCircle" iconPosition="left">
              Cancel Plan
            </Button>
          </div>
        </div>

        {/* Live Usage Section */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-foreground">Usage this billing cycle</h3>
            {showUpgrade && (
              <Link
                to="/account-settings?tab=billing"
                className="text-xs font-semibold text-primary border border-primary/30 px-3 py-1.5 rounded-lg hover:bg-primary/5 transition-colors"
              >
                Upgrade plan
              </Link>
            )}
          </div>

          {usageLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse space-y-1.5">
                  <div className="flex justify-between">
                    <div className="h-4 bg-muted rounded w-32" />
                    <div className="h-4 bg-muted rounded w-20" />
                  </div>
                  <div className="h-2 bg-muted rounded-full w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-5">
              <UsageRow
                label="Forms"
                icon="FileText"
                current={usage.forms}
                max={limits.maxForms}
                pct={formUsagePct()}
                showUpgrade={isAtFormLimit() || isNearFormLimit()}
              />
              <UsageRow
                label="Submissions this month"
                icon="Inbox"
                current={usage.submissions}
                max={limits.maxSubmissions}
                pct={submissionUsagePct()}
                showUpgrade={isAtSubLimit() || isNearSubLimit()}
              />
              <UsageRow
                label="Team seats"
                icon="Users"
                current={usage.teamMembers}
                max={teamMax}
                pct={teamPct}
                showUpgrade={isAtTeam || isNearTeam}
              />
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="HardDrive" size={15} />
                  <span>Storage capacity</span>
                </div>
                <span className="font-semibold text-foreground">
                  {formatStorageLimit(limits.maxStorageMb)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground -mt-3">
                Storage usage measurement will appear here when file-usage tracking is enabled.
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Available Plans */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Available Plans
          </h2>
          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                billingCycle === 'annual' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <span className="px-1.5 py-0.5 bg-success/20 text-success text-xs rounded-full font-bold">Billed annually</span>
            </button>
          </div>
        </div>

        {loadingPlans ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
            {[1, 2, 3, 4]?.map((i) => (
              <div key={i} className="border-2 border-border rounded-lg p-6 animate-pulse">
                <div className="h-5 bg-muted rounded w-1/2 mb-3" />
                <div className="h-4 bg-muted rounded w-3/4 mb-4" />
                <div className="h-10 bg-muted rounded w-1/3 mb-4" />
                <div className="space-y-2">
                  {[1, 2, 3, 4]?.map((j) => <div key={j} className="h-4 bg-muted rounded" />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
            {plans?.map((plan) => {
              const planId = plan?.planKey || (PLAN_ORDER.includes(plan?.id) ? plan.id : plan?.id);
              const displayPrice = billingCycle === 'monthly'
                ? plan?.price
                : (plan?.annualMonthlyPrice ?? Number(plan?.annualPrice || 0) / 12);
              const upgradeHref = getUpgradeHref(plan);

              return (
                <div
                  key={planId}
                  className={`relative bg-background border-2 rounded-lg p-6 transition-smooth cursor-pointer ${
                    selectedPlan === planId
                      ? 'border-primary' :'border-border hover:border-primary/50'
                  } ${plan?.isPopular ? 'shadow-elevation-lg' : ''}`}
                  onClick={() => setSelectedPlan(planId)}
                >
                  {plan?.isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                      {plan?.badgeText || 'Most Popular'}
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                      {plan?.tierName}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">{plan?.description}</p>
                    <div className="flex items-baseline justify-center gap-1">
                      {displayPrice ? (
                        <>
                          <span className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                            ${displayPrice}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            /month
                          </span>
                        </>
                      ) : (
                        <span className="text-2xl font-heading font-bold text-foreground">Custom</span>
                      )}
                    </div>
                    {billingCycle === 'annual' && plan?.price > 0 && (
                      <p className="text-xs text-success mt-1">
                        ${plan?.annualPrice}/year billed annually
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-5">
                    <span>{formatLimit(plan?.maxForms ?? -1)} forms</span>
                    <span>{formatLimit(plan?.maxSubmissions ?? -1)} submissions/mo</span>
                    <span>{formatLimit(plan?.maxTeamMembers ?? -1)} seats</span>
                    <span>{formatStorageLimit(plan?.maxStorageMb ?? -1)}</span>
                  </div>
                  <ul className="space-y-2.5 mb-6">
                    {(plan?.features || [])?.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon
                          name={feature?.included ? 'Check' : 'X'}
                          size={16}
                          color={feature?.included ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                          className="flex-shrink-0 mt-0.5"
                        />
                        <span className={`text-sm ${
                          feature?.included ? 'text-foreground' : 'text-muted-foreground line-through'
                        }`}>
                          {feature?.text}
                          {feature?.infoPopup && <FeatureInfoPopup text={feature?.infoPopup} />}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    variant={selectedPlan === planId ? 'default' : 'outline'}
                    size="default"
                    fullWidth
                  >
                    <Link to={upgradeHref} onClick={(e) => { e?.stopPropagation(); setSelectedPlan(planId); }}>
                      {plan?.ctaText || (plan?.price ? 'Upgrade' : 'Contact Sales')}
                    </Link>
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Payment Method */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-6">
          Payment Method
        </h2>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-border rounded-lg">
          <div className="flex items-center gap-4">
            <div className="w-12 h-8 bg-blue-600 rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">{paymentMethod?.type}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                •••• •••• •••• {paymentMethod?.last4}
              </p>
              <p className="text-xs text-muted-foreground">Expires {paymentMethod?.expiry}</p>
            </div>
            {paymentMethod?.isDefault && (
              <span className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-full font-medium">Default</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">Update</Button>
            <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50">Remove</Button>
          </div>
        </div>
      </div>
      {/* Invoices */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-6">
          Billing History
        </h2>
        {invoicesLoading ? (
          <div className="space-y-3">
            {[1, 2, 3]?.map((i) => (
              <div key={i} className="flex items-center justify-between p-4 border border-border rounded-lg animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-muted rounded-lg" />
                  <div className="space-y-2">
                    <div className="h-4 bg-muted rounded w-32" />
                    <div className="h-3 bg-muted rounded w-24" />
                  </div>
                </div>
                <div className="h-4 bg-muted rounded w-20" />
              </div>
            ))}
          </div>
        ) : invoicesError ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Icon name="AlertCircle" size={24} color="var(--color-error)" />
            <p className="text-sm text-muted-foreground">{invoicesError}</p>
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={loadInvoices}>
              Retry
            </Button>
          </div>
        ) : invoices?.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={22} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm font-medium text-foreground">No invoices yet</p>
            <p className="text-xs text-muted-foreground">
              Your receipts will appear here once you have a paid plan.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {invoices?.map((invoice) => {
              const formatted = (() => {
                try {
                  return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: invoice?.currency || 'USD',
                  })?.format(invoice?.amount ?? 0);
                } catch {
                  return `$${(invoice?.amount ?? 0)?.toFixed(2)}`;
                }
              })();
              const pdfUrl = invoice?.invoicePdf || invoice?.hostedInvoiceUrl;
              return (
                <div
                  key={invoice?.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={18} color="var(--color-muted-foreground)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{invoice?.number || invoice?.id}</p>
                      <p className="text-xs text-muted-foreground">
                        {invoice?.date
                          ? new Date(invoice?.date)?.toLocaleDateString('en-US', {
                              month: 'long', day: 'numeric', year: 'numeric'
                            })
                          : '–'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-foreground">{formatted}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      invoice?.status === 'paid' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}>
                      {invoice?.status}
                    </span>
                    {pdfUrl ? (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                      >
                        <Icon name="Download" size={14} />
                        PDF
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground px-3 py-1.5">–</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingTab;